import { Ext } from "../../Doc";
import type { GuideProps } from "../Guide";

/**
 * The member guide, Spanish translation of en.tsx. Every number comes from `limits`; every claim
 * about who can read what follows the threat model (PLAN.md 4.12). Keep it in step with en.tsx.
 */
export function GuideEs({ limits: l, link }: GuideProps) {
  return (
    <>
      <h1>Cómo funciona Vrelis</h1>
      <p className="lead">
        Vrelis es un servicio para enviar mensajes cifrados, cápsulas del tiempo, que solo se pueden leer a partir de un momento que tú eliges: todos los del
        piloto, o una sola persona. Antes de ese momento nadie puede leer una cápsula: ni la persona a la que escribiste, ni quienes gestionan Vrelis, ni tú.
      </p>
      <p className="warn">
        Este es un piloto cerrado. Descarga las cápsulas que quieras conservar (consulta más abajo) y guarda tu frase de recuperación.
      </p>

      <h2>Por qué se puede confiar en el bloqueo</h2>
      <p>
        El bloqueo no es una promesa de Vrelis. Es criptografía publicada más una señal temporal pública que Vrelis no controla. Esto es lo que lo sostiene, y
        lo que tendrías que creer para que fallara.
      </p>

      <h3>drand: un reloj público que firma la hora</h3>
      <ul>
        <li>
          <Ext href="https://drand.love"><b>drand</b></Ext> es una red gestionada por la{" "}
          <Ext href="https://www.cloudflare.com/leagueofentropy/">League of Entropy</Ext>, un grupo de organizaciones independientes (entre ellas Cloudflare, Protocol Labs, Kudelski
          Security, la EPFL y el University College London) que juntas publican un valor aleatorio y firmado cada 3 segundos. Los valores están numerados: ronda 1,
          ronda 2, y así sucesivamente, cada una para un momento conocido. Cualquiera puede leerlos desde varios relés públicos; nadie puede leer uno futuro,
          porque todavía no existe.
        </li>
        <li>
          Cada ronda se firma con criptografía de umbral (<Ext href="https://en.wikipedia.org/wiki/BLS_digital_signature">firmas BLS</Ext>): cada organización tiene una parte de una clave secreta, y una firma necesita
          la mayoría de las partes. Ninguna organización por sí sola, ni una minoría de ellas, puede producir la firma de una ronda antes de tiempo. La red
          funciona desde 2020; la cadena que usamos,{" "}
          <Ext href="https://api.drand.sh/v2/chains/52db9ba70e0cc0f6eaf7803dd07447a1f5477735fd3f661792ba94600c84e971/info">quicknet</Ext>, desde 2023.
          Su clave pública está fijada dentro de nuestro código, y cada señal que recibimos se comprueba con ella.
        </li>
        <li>
          El <b>cifrado con bloqueo temporal</b> (<Ext href="https://eprint.iacr.org/2023/189">tlock</Ext>, publicado por investigadores de Protocol Labs y
          Cloudflare en 2023) convierte ese reloj en un candado: un mensaje se
          cifra para un número de ronda, y la firma de esa ronda, una vez publicada, es la única clave que lo abre. Hasta que drand la publica, esa clave no
          existe en ningún lugar del mundo.
        </li>
      </ul>

      <h3>Cómo lo usa Vrelis</h3>
      <ul>
        <li>
          Tu navegador cifra el texto y las claves de tus archivos para la ronda de drand de la hora que elegiste; Nueva cápsula muestra el número de ronda
          antes de enviar. Una cápsula privada se cifra por segunda vez para la clave del destinatario, así que después de la hora solo esa persona puede abrirla.
        </li>
        <li>
          Nuestro servidor recibe solo el resultado sellado y lo almacena. No tiene ninguna clave que abra una cápsula: ni antes de la hora ni, en el caso de
          una cápsula privada, tampoco después. Una filtración de la base de datos expondría texto cifrado y metadatos, nunca un mensaje.
        </li>
        <li>
          Cuando llega la hora, tu navegador (o el abridor sin conexión) obtiene la firma de la ronda de uno de los cuatro relés públicos de drand, la verifica
          con la clave pública fijada y descifra. Vrelis no participa en ese paso.
        </li>
        <li>
          Cada cápsula está firmada con la clave de su autor (<Ext href="https://en.wikipedia.org/wiki/Secp256k1">secp256k1</Ext>, el mismo tipo de clave que
          usan Bitcoin y <Ext href="https://nostr.com">Nostr</Ext>), así que se detecta cualquier cápsula modificada. La clave se crea en tu navegador a partir
          de tus 12 palabras (
          <Ext href="https://github.com/bitcoin/bips/blob/master/bip-0039.mediawiki">BIP-39</Ext>) y nunca sale de él sin cifrar.
        </li>
      </ul>

      <h3>Si Vrelis desaparece</h3>
      <ul>
        <li>
          Los formatos son abiertos: una cápsula es un <Ext href="https://github.com/nostr-protocol/nips/blob/master/01.md">evento de Nostr</Ext> (un{" "}
          <Ext href="https://github.com/nostr-protocol/nips/blob/master/59.md">gift wrap de NIP-59</Ext> si es privada) cuyo contenido es un archivo{" "}
          <Ext href="https://age-encryption.org">age</Ext> con una estrofa <Ext href="https://github.com/drand/tlock-js">tlock</Ext>. Cualquier software que
          implemente estas especificaciones abiertas puede abrir tus cápsulas; el nuestro no es necesario.
        </li>
        <li>
          <b>Descargar</b> guarda las cápsulas en tu propio disco, junto con el abridor sin conexión: un único archivo HTML que las abre en tu computadora, sin
          ningún servidor de Vrelis, usando la señal de drand necesaria y, para una cápsula privada, tu clave. Una exportación completa se puede abrir sin
          vrelis.com si la señal necesaria está disponible y conservas los archivos y las claves que requiere.
        </li>
        <li>
          Si drand dejara de funcionar antes de producir la señal futura necesaria, esas cápsulas podrían quedar bloqueadas para siempre. Descargarlas por
          adelantado no elimina ese riesgo. Una cápsula cuya fecha de apertura ya pasó sigue necesitando su señal exacta y verificada: si esa señal no está en
          tu archivo ni disponible en un relé, la fecha por sí sola no te permite abrirla sin conexión. Conserva exportaciones completas y las claves que
          requieren.
        </li>
      </ul>

      <h3>Lo que tendrías que creer para que fallara</h3>
      <ul>
        <li>
          Que la mayoría de las organizaciones de drand se pongan de acuerdo para firmar una ronda antes de tiempo. Son independientes, están en varios países y
          la cadena es pública, así que una firma así sería visible para todos. Esta es la suposición de confianza de todo el sistema.
        </li>
        <li>
          Que la página web mienta. La página es código que Vrelis sirve a tu navegador; quien controle ese código podría leer el texto antes de que se selle, o
          tu clave después de que la desbloquees. Por eso las cápsulas se pueden descargar y abrir sin conexión, por eso se muestran las huellas de las claves
          para que las compares fuera de Vrelis, y por eso está prevista, después del piloto, una aplicación para teléfono que mantenga la clave fuera de la
          página web.
        </li>
        <li>
          Que la criptografía actual se rompa. La criptografía de curva elíptica, que usan tanto drand como tu clave, podría romperse con futuros{" "}
          <Ext href="https://en.wikipedia.org/wiki/Post-quantum_cryptography">computadoras cuánticas</Ext>.
          Para cápsulas que deben seguir selladas durante décadas, este es un riesgo real que el piloto no resuelve.
        </li>
      </ul>

      <h2>Cómo entrar</h2>
      <ol>
        <li>
          Si el administrador aprobó tu dirección, regístrate directamente con ella; si no, abre tu enlace de invitación y usa su dirección. Inicia sesión con
          esa cuenta de Google (Google solo le comunica a Vrelis tu nombre y tu correo) o con una contraseña (te enviamos un enlace por correo para confirmar
          la dirección). Más adelante puedes añadir una clave de acceso para iniciar sesión.
        </li>
        <li>
          Vrelis te ofrece crear tu <b>clave</b> en tu navegador: tu firma principal, que firma lo que envías y abre las cápsulas privadas que te envían.
          Puedes omitirlo y crearla más tarde en Cuenta, Claves; sin clave solo lees cápsulas públicas. Antes de que aparezca tu <b>frase de recuperación</b>{" "}
          (12 palabras que reconstruyen la clave en cualquier dispositivo), se te pide que te asegures de que nadie pueda ver ni grabar tu pantalla.
        </li>
        <li>Escribe las 12 palabras en papel. Se te pide que escribas tres de ellas y luego que vuelvas a iniciar sesión para confirmar que eres tú: con tu contraseña o una clave de acceso, o con Google para tu primera clave.</li>
        <li>
          Elige un PIN (al menos {l.pinDigits} dígitos) y crea una <Ext href="https://passkeys.dev/docs/intro/what-are-passkeys/">clave de acceso</Ext> (huella
          dactilar, rostro o el PIN de tu dispositivo). Juntos protegen la clave en este dispositivo
          y la copia que Vrelis guarda para pasar a un dispositivo nuevo. Si tu navegador no puede usar una clave de acceso de esta forma, la clave queda sin
          protección en ese navegador y no hay copia en el servidor; entonces la frase es la única forma de llevarla a otro dispositivo.
        </li>
      </ol>

      <h2>Tu frase de recuperación</h2>
      <ul>
        <li>Guárdala en papel, en un lugar seguro. No la fotografíes ni la guardes en una computadora o en un gestor de contraseñas.</li>
        <li>
          Quien tenga las 12 palabras <b>es</b> tú: puede firmar como tú y abrir tus cápsulas privadas. Si se filtran, usa <b>Restablecer clave</b> en Cuenta,
          Claves: escribes la frase, vuelves a iniciar sesión y la clave se revoca para siempre; nadie puede volver a enviar con ella ni a ella. Indicas desde
          cuándo pudo haber sido robada, y las cápsulas enviadas con ella desde entonces se marcan como «clave revocada». Luego creas una clave nueva con una
          frase nueva.
        </li>
        <li>
          Tus claves anteriores pueden ir a un <b>llavero</b>, cifrado con tu clave actual, para que las cápsulas enviadas a una clave antigua se sigan abriendo
          sin la frase antigua. También puedes guardar tú la frase antigua y añadirla al llavero más tarde.
        </li>
        <li>
          Si pierdes un dispositivo: inicia sesión en otro y usa tu clave de acceso y tu PIN (si tu clave de acceso se sincroniza) o tu frase. Si olvidas el
          PIN: usa la frase. Tras {l.pinAttempts} PIN incorrectos la copia del servidor se bloquea; la frase escribe una nueva.
        </li>
        <li>
          Si pierdes la frase <b>y</b> todos los dispositivos con tu clave, tus cápsulas privadas no se podrán abrir nunca, por nadie. Un administrador solo
          puede darte una identidad nueva. Revocar una clave también necesita la frase, así que sin ella la revoca un administrador por ti.
        </li>
      </ul>

      <h2>Escribir una cápsula</h2>
      <ul>
        <li>
          <b>Todos los del piloto</b> (pública) o <b>un miembro</b> (privada). Una cápsula pública la puede leer cualquier miembro después de su hora; una
          privada, solo la persona a la que escribiste, y tú si guardas una copia (activada por defecto).
        </li>
        <li>
          Cada cápsula necesita un <b>asunto</b>. Nunca se cifra: quien ve la cápsula lo lee de inmediato, y nuestro servidor también.
        </li>
        <li>
          La <b>introducción</b> es opcional: hasta {l.introChars} caracteres que se pueden leer antes de la hora, al hacer clic en la cápsula. En una cápsula
          pública la lee todo el mundo, también nuestro servidor. En una privada solo la lee el destinatario, con su clave; el servidor no la ve. El mensaje y
          sus archivos siguen bloqueados en ambos casos.
        </li>
        <li>
          Quién envió una cápsula privada va sellado para la clave del destinatario: lo ve en cuanto abre la cápsula con su clave, mucho antes de su hora.
          Quien tenga la cápsula sin esa clave solo ve el asunto y la hora. Nuestro servidor sabe qué cuenta la envió. Solo se lo comunica al destinatario,
          cifrado para su clave, para que la aplicación pueda comprobar que la clave que firmó la cápsula pertenece a esa cuenta; si no, muestra «Remitente no
          verificado» en lugar de un nombre.
        </li>
        <li>
          <b>Discreto</b> (cápsulas privadas): las listas del destinatario muestran «Remitente discreto» en lugar de tu nombre hasta que hace clic en la
          cápsula, así que nadie que mire por encima de su hombro ve quién le escribe. El destinatario igualmente puede ver que eres tú, antes de la hora.
        </li>
        <li>
          Elige cuándo se abre: en 5 minutos, una hora, un día, una semana, un año, o una fecha hasta el año 2125. La hora de apertura real se muestra antes de
          enviar; tiene una precisión de pocos segundos.
        </li>
        <li>
          Texto: hasta {l.publicText} para una cápsula pública y {l.privateText} para una privada. Archivos: hasta {l.files} archivos y, en total, {l.fileBytes} con Gratis o {l.planFileBytes} con Legacy o Start. Las fotos
          se reducen y pierden sus metadatos, incluida la ubicación.
        </li>
        <li>
          <b>Sellar y enviar</b> pide tu PIN y tu clave de acceso una vez, para firmar la cápsula. Después nadie puede cambiarla, y nadie puede leerla antes de
          su hora.
        </li>
        <li>
          Antes de escribir a alguien en privado, comprueba su clave: Nueva cápsula muestra su huella (16 caracteres). Si su clave cambió desde la última vez
          que este navegador la vio, Nueva cápsula te avisa y espera hasta que confirmes con esa persona, fuera de Vrelis, que la nueva clave es realmente suya.
        </li>
      </ul>

      <h2>Leer</h2>
      <ul>
        <li>
          <b>Públicas</b>: cápsulas públicas. <b>Recibidos</b>: cápsulas privadas para ti. <b>Enviados</b>: las tuyas. <b>En seguimiento</b>: cápsulas
          públicas que esperas. Sigue a un autor para ver solo sus cápsulas.
        </li>
        <li>
          <b>Ocultar</b> a un miembro: sus cápsulas públicas salen de Públicas, en todos tus dispositivos; para esa persona no cambia nada. <b>Bloquear</b> a un
          miembro: ya no puede poner cápsulas privadas en tus Recibidos (Nueva cápsula se lo indica antes de enviar); aún puedes seguirlo y leer sus cápsulas
          públicas. Sus cápsulas anteriores pasan a Recibidos, Bloqueados, y se siguen abriendo. Ambas opciones están en el menú de cada persona y en la página
          Personas, junto con quién te sigue. Vrelis solo responde de lo que recibe y entrega: nadie puede impedir que alguien cifre para tu clave pública
          fuera de Vrelis.
        </li>
        <li>
          Una cápsula privada no muestra nada hasta que la abres: <b>Abrir</b> pide tu PIN y luego muestra quién la envió y cuándo se abre, y el texto cuando
          llega ese momento.
        </li>
        <li>
          Tu clave sigue bloqueada; cada acción que la necesita pide el PIN. Para leer varias cápsulas seguidas, usa <b>Desbloquear durante … min</b> en la barra
          superior. <b>Bloquear</b> oculta todo lo privado de una vez; salir de la página también.
        </li>
        <li>
          En <a href="/account">Cuenta</a>, Privacidad: «Mostrar los remitentes en mis listas solo después de hacer clic en una cápsula» trata cada cápsula
          privada como discreta, en todos tus dispositivos.
        </li>
        <li>
          Las listas muestran 50 cápsulas a la vez; <b>Mostrar más</b> añade las siguientes. Vrelis las filtra y ordena (por hora de apertura o por cuándo las
          recibió), así que un filtro o un grupo envía a Vrelis los identificadores de los miembros que elegiste, nunca el grupo ni su nombre. Filtrar
          Recibidos por remitente necesita tu clave, omite las cápsulas discretas y está desactivado mientras los remitentes solo aparecen tras un clic.
        </li>
        <li>
          <b>Buscar</b> mira los asuntos, la introducción de las cápsulas públicas, quién escribió una cápsula pública y a quién enviaste. Nunca mira el texto
          abierto (solo lo tiene tu dispositivo) ni quién te envió una cápsula privada.
        </li>
      </ul>

      <h2>Sobre de respuesta, CC y Correspondencia</h2>
      <ul>
        <li>
          Un mensaje privado puede llevar un <b>sobre de respuesta</b>: pides a cada destinatario una respuesta privada para ti, sellada para tu clave.
          Pueden responder aunque no te sigan. Eliges si cada respuesta se abre a una hora que fijas tú (después de esa hora no se acepta ninguna respuesta)
          o a una hora que elige el destinatario. Pagas por adelantado la cápsula y la respuesta: 2 sellos por destinatario en lugar de 1. La primera respuesta de un destinatario no le cuesta nada; una segunda le cuesta un sello, y tú ves ambas.
        </li>
        <li>
          <b>CC</b>: un mensaje privado para un miembro puede ir a más miembros a la vez, y todos los incluidos ven quién más lo recibió. La lista va
          sellada dentro del mensaje con la firma del remitente; nuestro servidor nunca la ve. Un envío de grupo sin CC sigue siendo ciego: nadie ve
          quién más lo recibió.
        </li>
        <li>
          Los mensajes con sobre de respuesta y sus respuestas están en la página <b>Correspondencia</b>, no en Recibidos ni en Enviados: bajo cada
          solicitud ves en orden las respuestas de cada destinatario, bloqueadas hasta su hora, y quién pagó cada una. La respuesta solo le llega al remitente.
        </li>
      </ul>

      <h2>Conservar cápsulas sin Vrelis</h2>
      <ul>
        <li>
          <b>Descargar</b> en una cápsula la guarda como archivo; si tiene adjuntos, es un .zip con los archivos y el abridor sin conexión. Selecciona varias y
          usa{" "}
          <b>Descargar seleccionadas</b> para obtener un solo archivo comprimido («Seleccionar todas las mostradas» toma las cápsulas que se ven).
        </li>
        <li>
          El abridor sin conexión es un único archivo HTML (<a href="/account">Cuenta</a>, o dentro del .zip). Abre tus cápsulas en tu propia computadora, las privadas con tu frase. Nunca
          contacta con vrelis.com; solo pide al servicio público de tiempo de drand la señal de apertura si el archivo aún no la contiene.
        </li>
        <li>
          «Incluir el texto y los archivos abiertos» pone el texto y los archivos abiertos en la descarga sin cifrar: cualquiera que tenga el archivo puede
          leerlos.
        </li>
      </ul>

      <h2>Este dispositivo</h2>
      <ul>
        <li>
          <b>Bloquear</b>: quita la clave de la memoria. <b>Cerrar sesión</b>: termina esta sesión; la clave se queda en este dispositivo. <b>Cerrar todas las
          sesiones</b>: termina tus sesiones en todos los dispositivos. <b>Olvidar este dispositivo</b>: borra tu clave y todo lo que Vrelis guardó en este
          navegador.
        </li>
        <li>Una sesión dura {l.sessionDays} días.</li>
      </ul>

      <h2>Qué puede ver Vrelis y qué no</h2>
      <ul>
        <li>
          <b>Vrelis no puede leer</b> una cápsula antes de su hora, ni nunca una cápsula privada (no tiene ninguna clave para ella), ni tu clave, tu PIN o tu
          frase.
        </li>
        <li>
          <b>Vrelis sí ve</b>: tu nombre y tu correo (el propietario y los administradores ven el correo), quién envía una cápsula y a quién, cuándo se envió,
          cuándo se abre una cápsula pública, los tamaños y que una cápsula tiene archivos. Después de su hora, una cápsula pública la puede leer cualquier
          miembro, y por tanto también Vrelis.
        </li>
        <li>
          El llavero y las copias de tu clave se almacenan solo cifrados; Vrelis no puede abrirlos.
        </li>
        <li>
          Una firma demuestra qué clave escribió una cápsula. Una cápsula marcada como «clave revocada» fue firmada por una clave que su propietario declaró
          robada más tarde. Que la clave pertenezca a una persona depende de la lista de miembros y del nombre que el remitente puso en la
          cápsula. Comprueba las huellas con las personas a las que escribes en privado.
        </li>
      </ul>

      <h2>Planes y sellos</h2>
      <ul>
        <li>
          Un <b>sello</b> paga una entrega a un destinatario. Tu propia copia es gratuita. Gratis da {l.freeStamps} sellos al mes, {l.freeStorage} de almacenamiento y {l.fileBytes} de archivos por mensaje; Legacy
          ({l.legacyStamps} sellos al mes, {l.planStorage}, {l.planFileBytes}) se otorgó a los primeros miembros; Start daría {l.planStamps} sellos al mes. Los sellos de un mes no se acumulan, y se renuevan el
          primer día del mes siguiente (UTC). <b>Facturación</b> muestra lo que tienes, lo que te queda y lo que está previsto.
        </li>
        <li>
          Por ahora no se cobra nada y no se puede comprar nada: los planes son una vista previa, y un administrador activa una solicitud a mano. Lo que ya
          enviaste sigue siendo legible para ti y para las personas a las que lo enviaste, pase lo que pase con tu plan o tus sellos.
        </li>
        <li>
          La copia para ti es opcional y está activada por defecto. Si la desactivas, solo el destinatario podrá leer ese mensaje: tú no podrás leerlo, ni siquiera cuando se abra.
        </li>
      </ul>

      <h2>Límites del piloto</h2>
      <ul>
        <li>
          Como máximo {l.daily} entregas al día (tu propia copia es gratuita; una cápsula para un grupo cuenta una por miembro). Subidas: {l.uploadDaily} y {l.unusedFiles} archivos sin usar cada 24 horas;
          los archivos que no se usan en una cápsula en 24 horas se borran.
        </li>
        <li>Inicia sesión con Google, con una contraseña o con una clave de acceso. Sin pagos y sin red pública: las cápsulas se quedan dentro del piloto.</li>
        <li>
          Invitar a una dirección que ya tiene cuenta envía a ese miembro una solicitud de conexión; si la acepta, ambos se siguen mutuamente. Tu pantalla se
          ve igual en los dos casos, así que nadie puede usar las invitaciones para averiguar quién es miembro.
        </li>
      </ul>

      <h2>Para desarrolladores</h2>
      <p>
        Los servicios pueden leer y enviar cápsulas a través de la <a href="/developers">API de Vrelis</a>. Un servicio obtiene su propia cuenta mediante una
        invitación de servicio de un administrador; para todos los demás es un miembro normal. Crea su propia clave y firma cada cápsula por sí mismo; sus
        tokens de API pueden leer y enviar, pero no pueden gestionar claves ni métodos de inicio de sesión. Puedes bloquear u ocultar un servicio como a
        cualquier otro miembro.
      </p>

      <h2>Qué pedimos a los miembros del piloto</h2>
      <ol>
        <li>Envía una cápsula pública que se abra en una hora y una cápsula privada a otro miembro que se abra en un día.</li>
        <li>Lee las cápsulas públicas cuando se abran e intenta leer una antes: no deberías poder.</li>
        <li>Uno de los miembros: configurar su clave en un segundo dispositivo y leer allí sus Recibidos.</li>
        <li>Uno de los miembros: ocultar a otro miembro y comprobar que sus cápsulas desaparecen solo de su propia lista Públicas.</li>
      </ol>

      <h2>Problemas</h2>
      <p>
        Usa <b>Informar de un problema</b> al pie de cada página, o escribe a {link}. Cuenta qué hiciste, qué esperabas y qué pasó; una captura de pantalla
        ayuda. El servicio, sus límites y responsabilidades se describen en las <a href="/terms">Condiciones de uso</a>.
      </p>
    </>
  );
}
